// The editor's final proof page (?final=<token>, emailed from MWS Desk): the whole book and the cover in a viewer
// he can scroll, the front cover as a picture, and a download for each file. He can also suggest changes: pick a
// writer's pieces, a bio, the opening quote or the title/copyright lines and edit them in the writers' proof editor,
// or send Baz a note about anything else. Everything goes back to MWS Desk as a suggestion for Baz to accept; nothing
// here changes the book or reaches a writer.
import { useCallback, useEffect, useRef, useState } from "react";
import { ProofEditPanel, type EditorMode } from "./proof-editor";
import { EditorTour, tourSeen, type TourStep } from "./proof-tour";
import type { FinalField, FinalItemSummary, FinalTextResponse } from "./types";

type FinalFile = { kind: "interior" | "cover" | "front"; label: string; name: string; size: number; type: string };
type Sent = { item: string; label: string; at: string };
type Info = { ok: true; title: string; at: string; files: FinalFile[]; items: FinalItemSummary[]; sent: Sent[] } | { ok: false; errors: string[] };

const mb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(n >= 100 * 1024 * 1024 ? 0 : 1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const whenOf = (iso: string) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Inside the editor: the same steps writers get, said for the editor (and why his changes go to Baz first).
const EDIT_TOUR: TourStep[] = [
  { target: null, title: "Editing a piece", body: "Change words, line breaks, italics or the title, exactly as the writers do on their proofs. Your changes go to Baz as suggestions, so try anything: nothing is final until he accepts it." },
  { target: ".proof-blk", title: "Change words right in the text", body: "Each stanza (or paragraph) has its own box. Click in and type. Return makes a new line." },
  { target: ".proof-toolbar", title: "Italics, bold and Undo", body: "Select words, then press I or B. Undo takes back your last change, as many times as you like." },
  { target: ".proof-struct", title: "Stanza and paragraph breaks", body: "Join with above pulls a stanza onto the one before; Split here breaks one in two where the cursor is." },
  { target: ".proof-tab-edits", title: "Your edits", body: "Every change you've made in this piece, listed with the words crossed out and added." },
  { target: ".proof-meter", title: "The writer's page limit", body: "Writers must stay inside it. You can go over; Baz will check it still fits the page." },
  { target: ".proof-send", title: "Send to Baz", body: "Sends your version with your note. It arrives in MWS Desk marked as yours, with every change highlighted. Nothing is sent to the writer." },
];

export function FinalView({ onBack }: { onBack: () => void }) {
  const token = new URLSearchParams(window.location.search).get("final") ?? "";
  const [info, setInfo] = useState<Info | null>(null);
  const [tab, setTab] = useState<"interior" | "cover">("interior");
  const [touring, setTouring] = useState(false);
  const [pick, setPick] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const heading = useRef<HTMLHeadingElement | null>(null);

  const load = useCallback(() => fetch("/api/final/info", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }) })
    .then((r) => r.json() as Promise<Info>), [token]);

  useEffect(() => {
    const previous = document.title;
    document.title = "Final Proof | Method Writers Speak";
    load()
      .then((d) => {
        setInfo(d);
        if (d.ok && !d.files.some((f) => f.kind === "interior")) setTab("cover");
        if (d.ok && d.items.length) setPick(d.items[0].id);
      })
      .catch(() => setInfo({ ok: false, errors: ["We couldn't reach the server. Please refresh to try again."] }));
    return () => { document.title = previous; };
  }, [load]);
  useEffect(() => { if (info) heading.current?.focus(); }, [info]);
  // first visit: the walkthrough, once the page has its files (Skip tour / Esc ends it; "How this works" replays it)
  useEffect(() => {
    if (info?.ok && !tourSeen(token, "final-tour")) { const t = window.setTimeout(() => setTouring(true), 600); return () => window.clearTimeout(t); }
  }, [info, token]);

  const refreshSent = () => { load().then((d) => { if (d.ok) setInfo(d); }).catch(() => {}); };
  const url = (kind: string, dl = false) => `/api/final/file?k=${encodeURIComponent(token)}&f=${kind}${dl ? "&dl=1" : ""}`;
  const has = (kind: string) => info?.ok && info.files.some((f) => f.kind === kind);
  const canEdit = !!(info?.ok && info.items.length);
  const steps: TourStep[] = [
    { target: null, title: "A quick look around", body: "A minute: how to read the book and the cover, download them, and suggest changes. Skip it any time." },
    { target: ".final-tabs", title: "The book or the cover", body: "Switch between the whole book and the full cover (back, spine and front) here.", show: () => setTab("interior") },
    { target: ".proof-viewer", title: "Read it right here", body: "Scroll down to turn the pages. Use the + and − at the top of the viewer to zoom in, or the arrows to jump to a page.", show: () => setTab("interior") },
    { target: ".proof-open-link", title: "Bigger, or on a phone", body: "Opens it full screen in its own tab. On an iPhone or iPad, use this: the box above may stay blank there." },
    ...(has("front") ? [{ target: ".final-front", title: "The front cover on its own", body: "Just the front, cut at the spine, sized for email. This is the one to attach to an email or post.", show: () => setTab("cover") } as TourStep] : []),
    ...(canEdit ? [
      { target: ".final-pick", title: "Change something", body: "Pick what to change: a writer's pieces, a bio, the opening quote, or the title and copyright page lines. Press Open, and it opens in the same editor the writers used for their proofs." } as TourStep,
      { target: ".final-why", title: "Why it goes to Baz first", body: "Your changes come back to Baz in MWS Desk as suggestions marked with your name, every change highlighted. He sets them into the book, so nothing breaks the layout, and he decides whether a writer who already approved their proof should see your change. No writer is emailed from here." } as TourStep,
      { target: ".final-note", title: "Anything else", body: "For things you can't edit here (the cover, the order of the book, a picture), write Baz a note." } as TourStep,
    ] : []),
    { target: ".final-downloads", title: "Downloads", body: "A copy of each to keep. The book and the full cover are the printer's files, so they're large; the front cover picture is small." },
    { target: null, title: "When Baz sends a new version", body: "It shows here, at the same link. Reply to his email any time." },
  ];
  const when = info?.ok ? new Date(info.at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const item = info?.ok ? info.items.find((i) => i.id === editing) : undefined;
  const group = (k: FinalItemSummary["kind"][]) => (info?.ok ? info.items.filter((i) => k.includes(i.kind)) : []);
  const done = (label: string) => { setEditing(null); setNotice(`Sent to Baz: ${label}.`); refreshSent(); document.querySelector(".final-pick")?.scrollIntoView({ block: "center" }); };

  const editorMode = (it: FinalItemSummary): EditorMode => ({
    textUrl: "/api/final/text", editUrl: "/api/final/edit", extra: { item: it.id },
    heading: it.kind === "bio" ? `${it.label}: bio` : it.label,
    intro: it.kind === "bio" ? "Change the bio as it should print. It goes to Baz as a suggestion." :
      it.kind === "quote" ? "The opening quote on page 3 and the note about its author. Change what you like; it goes to Baz as a suggestion." :
      "Change words, line breaks, italics or titles. It goes to Baz as a suggestion, marked as yours; the writer isn't emailed.",
    noteLabel: "A note for Baz (optional): why, or anything he should know",
    sendLabel: "Send to Baz",
    savedText: "Baz will see this in MWS Desk, marked as yours, with every change highlighted. Nothing changes in the book until he accepts it.",
    tourSteps: EDIT_TOUR, tourPrefix: "final-edit-tour",
  });

  return (
    <main className="page proof-page">
      <div className="proof-shell final-shell">
        <div className="proof-kicker">Method Writers Speak</div>
        {!info && <p className="proof-status">Loading…</p>}
        {info && !info.ok && (
          <div className="proof-message">
            <h1 ref={heading} tabIndex={-1} className="proof-title">That link didn't work</h1>
            <p className="proof-body">{info.errors[0]}</p>
            <button type="button" className="proof-link" onClick={onBack}>Back to the main site</button>
          </div>
        )}
        {info?.ok && (
          <>
            <h1 ref={heading} tabIndex={-1} className="proof-title">{info.title}: the final proof</h1>
            <p className="proof-titles">Ready {when}. Scroll through the book and the cover below{canEdit ? ", suggest changes," : ""} and download everything at the bottom.
              {" "}<button type="button" className="proof-link final-howto" onClick={() => setTouring(true)}>How this works</button></p>

            {has("interior") && has("cover") && (
              <div className="final-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={tab === "interior"} className={tab === "interior" ? "on" : ""} onClick={() => setTab("interior")}>The book</button>
                <button type="button" role="tab" aria-selected={tab === "cover"} className={tab === "cover" ? "on" : ""} onClick={() => setTab("cover")}>The cover</button>
              </div>
            )}

            {has(tab) && (
              <>
                <div className="proof-viewer">
                  <object key={tab} data={url(tab)} type="application/pdf" className="proof-object" aria-label={tab === "interior" ? "The book" : "The cover"}>
                    <p className="proof-fallback">
                      Your browser can't show the PDF here. <a href={url(tab)} target="_blank" rel="noreferrer">Open it in a new tab</a>.
                    </p>
                  </object>
                </div>
                <p className="proof-open-link">
                  <a className="proof-link" href={url(tab)} target="_blank" rel="noreferrer">Open {tab === "interior" ? "the book" : "the cover"} in a new tab</a>
                </p>
              </>
            )}

            {tab === "cover" && has("front") && (
              <figure className="final-front">
                <img src={url("front")} alt={`The front cover of ${info.title}`} />
                <figcaption>The front cover on its own, at web size: ready to attach to an email or post.</figcaption>
              </figure>
            )}

            {canEdit && (
              <section className="final-changes">
                <h2 className="final-h2">Suggest changes</h2>
                <p className="proof-body final-why">Anything you change here goes to Baz as a suggestion, marked as yours, with every change
                  highlighted. He sets it into the book, so the layout stays right, and if a writer has already approved their
                  proof he decides whether they should see your change. No writer is emailed from this page.</p>
                <div className="final-pick">
                  <label className="proof-label" htmlFor="final-pick">What would you like to change?</label>
                  <div className="final-pick-row">
                    <select id="final-pick" value={pick} onChange={(e) => setPick(e.target.value)} disabled={!!editing}>
                      {group(["quote", "lines"]).length > 0 && <optgroup label="Opening pages">{group(["quote", "lines"]).map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}</optgroup>}
                      {group(["writer"]).length > 0 && <optgroup label="Writers' pieces">{group(["writer"]).map((i) => <option key={i.id} value={i.id}>{i.label}{i.sub ? `: ${i.sub}` : ""}</option>)}</optgroup>}
                      {group(["bio"]).length > 0 && <optgroup label="Bios">{group(["bio"]).map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}</optgroup>}
                    </select>
                    <button type="button" className="proof-btn proof-btn--ok" disabled={!pick || !!editing} onClick={() => { setNotice(""); setEditing(pick); }}>Open</button>
                  </div>
                </div>
                {notice && <p className="proof-answered" role="status">{notice}</p>}
                {item && (item.kind === "lines"
                  ? <FieldsPanel key={item.id} token={token} item={item} onCancel={() => setEditing(null)} onSent={() => done(item.label)} />
                  : <ProofEditPanel key={item.id} proof={{ token }} editor={editorMode(item)}
                      onCancel={() => setEditing(null)} onSent={() => done(item.kind === "bio" ? `${item.label}'s bio` : item.label)} onClosed={() => setEditing(null)} />)}
                <NotePanel token={token} onSent={() => { setNotice("Your note is with Baz."); refreshSent(); }} />
                {info.sent.length > 0 && (
                  <div className="final-sent">
                    <h3>What you've sent</h3>
                    <ul>{info.sent.map((x, k) => <li key={k}><span>{x.label}</span><span className="final-size">{whenOf(x.at)}</span></li>)}</ul>
                  </div>
                )}
              </section>
            )}

            <h2 className="final-h2">Downloads</h2>
            <ul className="final-downloads">
              {info.files.map((f) => (
                <li key={f.kind}>
                  <a className="proof-link" href={url(f.kind, true)} download={f.name}>{f.label}</a>
                  <span className="final-size">{f.kind === "front" ? "JPEG" : "PDF"} · {mb(f.size)}</span>
                </li>
              ))}
            </ul>
            <p className="proof-body proof-body--muted">The book and cover PDFs are the files that go to the printer, so they are large.
              The front cover JPEG is small enough to attach to an email.</p>
          </>
        )}
      </div>
      {touring && <EditorTour token={token} prefix="final-tour" steps={steps} onClose={() => setTouring(false)} />}
    </main>
  );
}

/** The title and copyright page lines (and credits) as plain fields. */
function FieldsPanel({ token, item, onCancel, onSent }: { token: string; item: FinalItemSummary; onCancel: () => void; onSent: () => void }) {
  const [fields, setFields] = useState<FinalField[] | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetch("/api/final/text", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, item: item.id }) })
      .then((r) => r.json() as Promise<FinalTextResponse | { ok: false; errors: string[] }>)
      .then((d) => {
        if (!d.ok) { setError(d.errors[0] ?? "Couldn't open it."); return; }
        const f = d.item.fields ?? [];
        setFields(f);
        setValues(Object.fromEntries(f.map((x) => [x.key, x.value])));
      })
      .catch(() => setError("We couldn't reach the server. Please try again."));
  }, [token, item.id]);
  const changed = fields?.some((f) => (values[f.key] ?? "").trim() !== f.value.trim());
  const send = async () => {
    setBusy(true); setError("");
    try {
      const res = await fetch("/api/final/edit", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, item: item.id, fields: Object.entries(values).map(([key, value]) => ({ key, value })), note: note.trim() }) });
      const d = (await res.json()) as { ok: boolean; errors?: string[] };
      if (d.ok) onSent(); else setError(d.errors?.[0] ?? "Couldn't send it.");
    } catch { setError("We couldn't reach the server. Please try again."); } finally { setBusy(false); }
  };
  if (!fields) return <p className="proof-status">{error || "Opening…"}</p>;
  return (
    <div className="proof-edit final-fields">
      <h2 className="proof-edit-heading">{item.label}</h2>
      <p className="proof-body">Change a line as it should print; leave the rest. {"<i>…</i>"} around words makes them italic.</p>
      {fields.map((f) => (
        <label key={f.key} className="final-field">
          <span>{f.label}</span>
          {f.multiline
            ? <textarea className="proof-textarea" rows={Math.min(8, Math.max(3, f.value.split("\n").length + 1))} value={values[f.key] ?? ""} disabled={busy} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
            : <input className="final-input" value={values[f.key] ?? ""} disabled={busy} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />}
        </label>
      ))}
      <label className="proof-label" htmlFor="final-fields-note">A note for Baz (optional)</label>
      <textarea id="final-fields-note" className="proof-textarea" rows={2} maxLength={2000} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
      {error && <p className="proof-error" role="alert">{error}</p>}
      <div className="proof-actions">
        <button type="button" className="proof-btn proof-btn--ok" disabled={busy || (!changed && !note.trim())} onClick={() => void send()}>{busy ? "Sending…" : "Send to Baz"}</button>
        <button type="button" className="proof-link" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** A note to Baz about anything that can't be edited here (the cover, the order, a picture). */
function NotePanel({ token, onSent }: { token: string; onSent: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const send = async () => {
    setBusy(true); setError("");
    try {
      const res = await fetch("/api/final/edit", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, item: "note", note: note.trim() }) });
      const d = (await res.json()) as { ok: boolean; errors?: string[] };
      if (d.ok) { setNote(""); onSent(); } else setError(d.errors?.[0] ?? "Couldn't send it.");
    } catch { setError("We couldn't reach the server. Please try again."); } finally { setBusy(false); }
  };
  return (
    <div className="final-note">
      <label className="proof-label" htmlFor="final-note">A note to Baz about anything else (the cover, the order of the book, a picture)</label>
      <textarea id="final-note" className="proof-textarea" rows={3} maxLength={2000} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
      {error && <p className="proof-error" role="alert">{error}</p>}
      <button type="button" className="proof-btn proof-btn--edit" disabled={busy || !note.trim()} onClick={() => void send()}>{busy ? "Sending…" : "Send the note"}</button>
    </div>
  );
}
